import i18n from './index';

/**
 * Egyptian formats for both languages: day/month/year, Latin digits (what
 * Egyptian HR reads on every official form), EGP with two decimals.
 *
 * Arabic keeps Latin digits deliberately — `ar-EG` alone renders Arabic-Indic
 * numerals, which are not how payroll figures are written here.
 */
const locale = () => (i18n.language.startsWith('ar') ? 'ar-EG-u-nu-latn' : 'en-EG');

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function toDate(value: string | Date | number): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  // A bare date is a calendar day, not an instant: never shift it by time zone.
  if (ISO_DATE.test(value)) return new Date(`${value}T00:00:00`);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Today's calendar date in Cairo, as YYYY-MM-DD — the company's day, not
 * UTC's: `toISOString()` is still yesterday until 02:00 or 03:00 Cairo time.
 */
export function cairoToday(now: Date = new Date()): string {
  // en-CA formats a date as YYYY-MM-DD.
  return now.toLocaleDateString('en-CA', { timeZone: 'Africa/Cairo' });
}

/** Default report ranges; pure, so they live apart for tests. */
export { calendarYear, lastTwelveMonths } from './dateRange';

/** `datetime-local` ⇄ instant in Cairo's zone; pure, so it lives apart for tests. */
export { cairoLocalToInstant, cairoOffsetMinutes, instantToCairoLocal } from './cairoTime';

/**
 * Instants are shown on Cairo's clock whatever the browser's zone, matching
 * `cairoToday()` and the punches the API stores in UTC. A bare calendar date
 * is parsed as local midnight above and must be formatted locally too, or a
 * browser east of Cairo would show the day before.
 */
function zoneOf(value: string | Date): { timeZone?: string } {
  return typeof value === 'string' && ISO_DATE.test(value) ? {} : { timeZone: 'Africa/Cairo' };
}

/** 03/09/2026 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d
    ? d.toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric', ...zoneOf(value) })
    : String(value);
}

/** 03/09/2026, 16:44 */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d
    ? d.toLocaleString(locale(), {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...zoneOf(value),
      })
    : String(value);
}

/** Sun — the short weekday name, for headers over a run of dates. */
export function formatWeekday(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d ? d.toLocaleDateString(locale(), { weekday: 'short', ...zoneOf(value) }) : '';
}

/** 16:44 */
export function formatTime(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = toDate(value);
  return d
    ? d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit', hour12: false, ...zoneOf(value) })
    : String(value);
}

/** 12,345.5 — grouping, up to two decimals. */
export function formatNumber(value: number | string | null | undefined, digits = 2): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString(locale(), { maximumFractionDigits: digits });
}

/** 12,345.50 — always two decimals, for money. */
export function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  return Number.isNaN(n) ? String(value) : n.toLocaleString(locale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Grid cells: an ISO date or instant from the API, shown the Egyptian way. */
export function formatCell(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  if (ISO_DATE.test(value)) return formatDate(value);
  if (ISO_DATETIME.test(value)) return formatDateTime(value);
  return value;
}
